import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import type { Category } from "models";
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { db } from 'firebase-utils/firebase';
import { useOrg } from 'contexts/orgContext';
import { logger } from 'utils/logger';

interface CategoriesContextType {
  categories: Category[];
  loading: boolean;
}

const CategoriesContext = createContext<CategoriesContextType>({
  categories: [],
  loading: true,
});

export const CategoriesProvider = ({ children }: { children: ReactNode }) => {
  const { currentOrg } = useOrg();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (!currentOrg) return;

    const categoriesRef = collection(db, `orgs/${currentOrg?.id}/categories`);
    const q = query(categoriesRef, orderBy('order', 'asc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedCategories: Category[] = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      } as Category));
      setCategories(fetchedCategories);
      setLoading(false);
    }, (error) => {
      logger.error('Error fetching categories', { context: 'CategoriesContext', error });
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentOrg]);

  return (
    <CategoriesContext.Provider value={{ categories, loading }}>
      {children}
    </CategoriesContext.Provider>
  );
};

export const useCategories = () => useContext(CategoriesContext);
